import type { ModuleFederationRuntimePlugin } from '@module-federation/enhanced/runtime'
import { datadogRum } from '@datadog/browser-rum'
import { MFE_URL_PLACEHOLDER } from './mfeRemotes'
import { getDefaultUrl } from './mfeUrls'

const MFE_ENTRY_FILE = /\/(remoteEntry\.js|mf-manifest\.json)$/
const LOCAL_HOSTNAMES = ['localhost', '127.0.0.1', '[::1]']

// Overrides come from page-writable sources (localStorage, window), so only build defaults,
// same-origin MFE paths, and localhost (mfe-chrome-plugin testing) may be loaded.
// Keep in sync with the copies in contrast-ui-react and udp-client.
export const isAllowedRemoteUrl = (
  url: string,
  defaultUrl: string | undefined,
  pageOrigin: string = window.location.origin
): boolean => {
  if (url === defaultUrl) {
    return true
  }

  let parsed: URL
  try {
    parsed = new URL(url, pageOrigin)
  } catch {
    return false
  }

  if (LOCAL_HOSTNAMES.includes(parsed.hostname)) {
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
  }

  return (
    parsed.origin === pageOrigin &&
    parsed.pathname.startsWith('/Contrast/mfe/') &&
    MFE_ENTRY_FILE.test(parsed.pathname)
  )
}

const mfeDynamicUrlPlugin = (): ModuleFederationRuntimePlugin => ({
  name: 'mfe-dynamic-url-plugin',
  // Must patch the registered remote before MF's snapshot plugin checks its entry for a manifest.
  beforeRequest(args) {
    const urls = window.getUrl()
    const defaultUrls = getDefaultUrl()
    args.options.remotes.forEach((remote) => {
      if (
        !('entry' in remote) ||
        !remote.entry.includes(MFE_URL_PLACEHOLDER) ||
        !urls[remote.name]
      ) {
        return
      }

      const url = urls[remote.name]
      const defaultUrl = defaultUrls[remote.name]
      if (isAllowedRemoteUrl(url, defaultUrl)) {
        remote.entry = url
        return
      }

      const err = new Error(`[MFE] Rejected remote URL for "${remote.name}"`)
      // eslint-disable-next-line no-console
      console.warn(err.message, url)
      datadogRum.addError(err, {
        mfeRemoteId: remote.name,
        mfeRejectedUrl: url
      })
      if (defaultUrl) {
        remote.entry = defaultUrl
      }
    })
    return args
  },
  errorLoadRemote({ id, error, lifecycle }) {
    const err = error instanceof Error ? error : new Error(String(error))
    // eslint-disable-next-line no-console
    console.error(`[MFE] Failed to load remote "${id}" (${lifecycle}):`, err)
    datadogRum.addError(err, { mfeRemoteId: id, mfeLifecycle: lifecycle })
    // RemoteComponent (design system) is an Error Boundary — catches failed load and renders fallback
  }
})

// eslint-disable-next-line import/no-default-export
export default mfeDynamicUrlPlugin
